import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { VolumeX } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAmbience } from "@/context/AmbienceContext";

export const SoundToggle = () => {
  const { t } = useLang();
  const { playing, volume, toggle, setVolume } = useAmbience();
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 2, duration: 0.8 }}
      className="fixed bottom-6 left-6 z-[55] flex items-center"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      <div
        className={`flex items-center gap-3 border backdrop-blur-xl rounded-full pr-2 transition-colors duration-500 ${
          playing ? "border-[#c5a059] bg-[#c5a059]/15" : "border-white/20 bg-[#050706]/70 hover:border-[#c5a059]/60"
        }`}
      >
        <button
          onClick={toggle}
          data-testid="sound-toggle"
          aria-label={playing ? "Mute ambience" : "Play ambience"}
          className={`flex items-center gap-3 pl-4 py-3 rounded-full ${playing ? "text-[#c5a059]" : "text-[#8a9690] hover:text-[#c5a059]"} transition-colors duration-300`}
        >
          {playing ? (
            <span className="flex items-end gap-[3px] h-4" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className="sound-bar w-[3px] bg-[#c5a059] rounded-full" style={{ animationDelay: `${i * 0.18}s` }} />
              ))}
            </span>
          ) : (
            <VolumeX size={16} />
          )}
          <span className="text-[10px] tracking-[0.25em] uppercase whitespace-nowrap">
            {playing ? t.sound.on : t.sound.ambience}
          </span>
        </button>
        <AnimatePresence>
          {(expanded || playing) && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: "auto", opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden flex items-center"
            >
              <input
                type="range"
                data-testid="sound-volume-slider"
                aria-label={t.sound.volume}
                className="gold-slider w-20 mx-2"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
